<script>
	import { base } from '$app/paths';
	import { onMount } from 'svelte';
	import SeoHead from '../../common/SeoHead.svelte';
	import links from './publicDocLinks.json';
	import ProviderCatalogue from './ProviderCatalogue.svelte';
	let { document } = $props();
	let search = $state('');
	let searchIndex = $state({});
	async function loadSearch() {
		try {
			searchIndex = (await import('./manualSearch.json')).default;
		} catch {
			/* Chapter titles remain searchable when offline without the index. */
		}
	}
	let navigationOpen = $state(true);
	onMount(() => {
		const wide = window.matchMedia('(min-width: 1001px)');
		const update = () => {
			navigationOpen = wide.matches;
		};
		update();
		wide.addEventListener('change', update);
		return () => wide.removeEventListener('change', update);
	});
	let manual = $derived(document.section === 'manual');
	let chapters = $derived(links.filter((link) => link.section === 'manual'));
	let siblings = $derived(links.filter((link) => link.section === document.section));
	let index = $derived(siblings.findIndex((link) => link.path === document.path));
	let matches = $derived(
		chapters.filter((link) =>
			(searchIndex[link.slug] || link.label.toLowerCase()).includes(search.toLowerCase().trim())
		)
	);
	let schema = $derived(
		JSON.stringify({
			'@context': 'https://schema.org',
			'@graph': [
				...(document.slug === 'about'
					? [
							{
								'@type': 'SoftwareApplication',
								name: 'Cinema Sign',
								applicationCategory: 'MultimediaApplication',
								operatingSystem: 'Android TV',
								softwareVersion: '4.1',
								url: `https://tools.d-cine.net${base}/cinema-sign/about/`,
								downloadUrl: 'https://www.d-cine.net/cinema-sign-apk/get.php',
								description: document.description,
								image: `https://tools.d-cine.net${base}${document.image}`,
								author: { '@type': 'Person', name: 'James Gardiner' }
							}
						]
					: []),
				{
					'@type': manual ? 'TechArticle' : 'WebPage',
					name: document.title,
					headline: document.title,
					description: document.description,
					url: `https://tools.d-cine.net${base}${document.path}`,
					dateModified: document.updated,
					inLanguage: 'en',
					image: `https://tools.d-cine.net${base}${document.image}`,
					author: { '@type': 'Organization', name: 'D-Cine' }
				},
				{
					'@type': 'BreadcrumbList',
					itemListElement: [
						{
							name: 'Cinema Sign',
							item: `https://tools.d-cine.net${base}/cinema-sign/about/`
						},
						...(manual && document.slug !== 'index'
							? [
									{
										name: 'User manual',
										item: `https://tools.d-cine.net${base}/cinema-sign/manual/`
									}
								]
							: []),
						{ name: document.label, item: `https://tools.d-cine.net${base}${document.path}` }
					].map((item, i) => ({ '@type': 'ListItem', position: i + 1, ...item }))
				}
			]
		}).replace(/</g, '\\u003c')
	);
</script>

<SeoHead
	title={document.title}
	description={document.description}
	path={document.path}
	image={document.image}
	type={manual ? 'article' : 'website'}
/>
<svelte:head>{@html `<script type="application/ld+json">${schema}</script>`}</svelte:head>

{#if manual}
	<div class="wrap manual-layout">
		<aside class="manual-contents">
			<details class="chapter-menu" bind:open={navigationOpen}>
				<summary>Manual chapters</summary>
				<label class="manual-search"
					>Search chapters<input
						type="search"
						bind:value={search}
						onfocus={loadSearch}
						oninput={loadSearch}
					/></label
				>
				<nav class="chapter-links" aria-label="Manual chapters">
					{#each matches as link}<a
							href={`${base}${link.path}`}
							aria-current={link.path === document.path ? 'page' : undefined}>{link.label}</a
						>{/each}
				</nav>
				{#if !matches.length}<p>No matching chapters.</p>{/if}
			</details>
			{#if document.toc.length}<details open>
					<summary>On this page</summary>
					<nav class="chapter-links" aria-label="On this page">
						{#each document.toc as heading}<a href={`#${heading.id}`}>{heading.title}</a>{/each}
					</nav>
				</details>{/if}
		</aside>
		<div class="manual-body">
			<div class="manual-tools">
				<button
					type="button"
					title="Print this manual page"
					aria-label="Print this manual page"
					onclick={() => window.print()}><i class="bx bx-printer" aria-hidden="true"></i></button
				>
			</div>
			<article class="doc-content">{@html document.html.replaceAll('__APP_BASE__', base)}</article>
			{#if document.slug === 'index'}<ol class="chapter-index">
					{#each chapters.filter((link) => link.slug !== 'index') as link}<li>
							<a href={`${base}${link.path}`}>{link.label.substring(3)}</a>
						</li>{/each}
				</ol>{/if}
			<nav class="doc-paging" aria-label="Adjacent chapters">
				{#if siblings[index - 1]}<a href={`${base}${siblings[index - 1].path}`}
						><i class="bx bx-left-arrow-alt" aria-hidden="true"></i> {siblings[index - 1].label}</a
					>{/if}
				{#if siblings[index + 1]}<a href={`${base}${siblings[index + 1].path}`}
						>{siblings[index + 1].label} <i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
					>{/if}
			</nav>
		</div>
	</div>
{:else if document.cards?.length}
	<article class="doc-content">
		{@html document.before.replaceAll('__APP_BASE__', base)}
		<section id="catalogue" class="section alt">
			<div class="wrap">
				{@html document.catalogueHeading.replaceAll('__APP_BASE__', base)}<ProviderCatalogue
					cards={document.cards}
				/>
			</div>
		</section>
		{@html document.after.replaceAll('__APP_BASE__', base)}
	</article>
{:else}
	<article class="doc-content">{@html document.html.replaceAll('__APP_BASE__', base)}</article>
{/if}
